/**
 * ChallengeZoneBlock — Challenge zone sidebar card: scavenger hunt, mini leaderboard, or bingo.
 *
 * Renders the right-column (~35%) sidebar from the CouRageJD/7-Eleven wireframes.
 * Three variant mechanics share a common card shell (header, reward row, CTA, more activations).
 *
 * Layout:
 *   ┌─────────────────────────────┐
 *   │ Logo + Brand + Type + Timer │  ← Card header
 *   ├─────────────────────────────┤
 *   │ Mechanic content            │  ← Variant-specific
 *   │ (checklist / LB / bingo)    │
 *   ├─────────────────────────────┤
 *   │ Reward row                  │
 *   ├─────────────────────────────┤
 *   │ CTA Button                  │
 *   ├─────────────────────────────┤
 *   │ More Activations (mini)     │
 *   └─────────────────────────────┘
 */

import { useEffect, useState } from "react";
import "./ChallengeZoneBlock.scss";

interface ChallengeZoneObjective {
    label: string;
    sublabel?: string;
    tag?: "BRAND" | "GAME" | "REWARD";
    state: "unchecked" | "partial" | "done";
}

interface ChallengeZoneLeaderEntry {
    rank: number;
    avatarUrl?: string;
    name: string;
    score: number;
    isCurrentUser?: boolean;
}

interface ChallengeZoneBingoCell {
    label: string;
    state: "unchecked" | "done" | "free";
}

interface ChallengeZoneMoreActivation {
    logoUrl?: string;
    brandName: string;
    type: string;
}

/**
 * Must stay in sync with ChallengeZoneConfig in admin/src/pages/hub-builder/fields/ChallengeZoneConfigField.tsx.
 * Both interfaces are typed locally (web does not import from @devour/models) — update them together.
 */
interface ChallengeZoneConfig {
    variant: "SCAVENGER_HUNT" | "LEADERBOARD_MINI" | "BINGO_MINI";
    brandLogoUrl?: string;
    brandName?: string;
    challengeType?: string;
    countdownEndAt?: number;
    rewardIcon?: string;
    rewardLabel?: string;
    rewardDescription?: string;
    ctaText?: string;
    ctaUrl?: string;
    moreActivations?: ChallengeZoneMoreActivation[];
    objectives?: ChallengeZoneObjective[];
    leaderboardEntries?: ChallengeZoneLeaderEntry[];
    bingoCells?: ChallengeZoneBingoCell[];
    bingoPerLineReward?: string;
}

interface ChallengeZoneBlockProps {
    config?: ChallengeZoneConfig;
    sectionTitle?: string;
}

const TAG_COLORS: Record<string, string> = {
    BRAND: "#585858",
    GAME: "#2563EB",
    REWARD: "#D97706",
};

export function ChallengeZoneBlock({ config, sectionTitle }: ChallengeZoneBlockProps) {
    if (!config) return null;

    const doneObjectives = config.objectives?.filter(o => o.state === "done").length ?? 0;
    const totalObjectives = config.objectives?.length ?? 0;
    const progressPct = totalObjectives > 0 ? Math.round(doneObjectives / totalObjectives * 100) : 0;

    const completedBingo = config.bingoCells?.filter(c => c.state === "done").length ?? 0;
    const totalBingo = config.bingoCells?.length ?? 25;

    return (
        <div className="czb">
            {sectionTitle && <h2 className="section-title">{sectionTitle}</h2>}

            <div className="czb__card">
                {/* Card Header */}
                <div className="czb__header">
                    <div className="czb__header-left">
                        {config.brandLogoUrl &&
                            <img src={config.brandLogoUrl} alt={config.brandName || "Brand"} className="czb__brand-logo" />
                        }
                        <div className="czb__header-text">
                            {config.brandName && <span className="czb__brand-name">{config.brandName}</span>}
                            {config.challengeType &&
                                <span className="czb__challenge-type">{config.challengeType.toUpperCase()}</span>
                            }
                        </div>
                    </div>
                    {config.countdownEndAt &&
                        <CountdownTimer endAt={config.countdownEndAt} />
                    }
                </div>

                {/* Mechanic Content */}
                <div className="czb__mechanic">
                    {config.variant === "SCAVENGER_HUNT" &&
                        <ScavengerHuntMechanic
                            objectives={config.objectives || []}
                            progressPct={progressPct}
                            doneCount={doneObjectives}
                            total={totalObjectives}
                        />
                    }
                    {config.variant === "LEADERBOARD_MINI" &&
                        <LeaderboardMiniMechanic entries={config.leaderboardEntries || []} />
                    }
                    {config.variant === "BINGO_MINI" &&
                        <BingoMiniMechanic
                            cells={config.bingoCells || []}
                            perLineReward={config.bingoPerLineReward}
                            completed={completedBingo}
                            total={totalBingo}
                        />
                    }
                </div>

                {/* Reward Row */}
                {(config.rewardDescription || config.rewardLabel) &&
                    <div className="czb__reward-row">
                        {config.rewardIcon && <i className={`fas fa-${config.rewardIcon} czb__reward-icon`} />}
                        <div className="czb__reward-text">
                            {config.rewardLabel && <span className="czb__reward-label">{config.rewardLabel}</span>}
                            {config.rewardDescription &&
                                <span className="czb__reward-desc">{config.rewardDescription}</span>
                            }
                        </div>
                    </div>
                }

                {/* CTA Button */}
                {config.ctaText &&
                    <a
                        href={config.ctaUrl || "#"}
                        className="czb__cta"
                        {...(config.ctaUrl ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    >
                        {config.ctaText}
                    </a>
                }

                {/* More Activations */}
                {config.moreActivations && config.moreActivations.length > 0 &&
                    <div className="czb__more">
                        <span className="czb__more-label">More Activations</span>
                        <div className="czb__more-list">
                            {config.moreActivations.slice(0, 3).map((act, i) =>
                                <div key={i} className="czb__more-item">
                                    {act.logoUrl && <img src={act.logoUrl} alt={act.brandName} className="czb__more-logo" />}
                                    <div className="czb__more-item-text">
                                        <span className="czb__more-brand">{act.brandName}</span>
                                        <span className="czb__more-type">{act.type}</span>
                                    </div>
                                </div>)}
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}

// ─── Scavenger Hunt ────────────────────────────────────────────────────────────

function ScavengerHuntMechanic({ objectives, progressPct, doneCount, total }: {
    objectives: ChallengeZoneObjective[];
    progressPct: number;
    doneCount: number;
    total: number;
}) {
    return (
        <div className="czb__scavenger">
            <div className="czb__objective-list">
                {objectives.map((obj, i) =>
                    <div key={i} className={`czb__objective czb__objective--${obj.state}`}>
                        <span className={`czb__obj-check czb__obj-check--${obj.state}`}>
                            {obj.state === "done" && "✓"}
                            {obj.state === "partial" && "~"}
                        </span>
                        <div className="czb__obj-text">
                            <span className="czb__obj-label">{obj.label}</span>
                            {obj.sublabel && <span className="czb__obj-sublabel">{obj.sublabel}</span>}
                        </div>
                        {obj.tag &&
                            <span
                                className="czb__obj-tag"
                                style={{ backgroundColor: `${TAG_COLORS[obj.tag]}22`, color: TAG_COLORS[obj.tag], borderColor: `${TAG_COLORS[obj.tag]}44` }}
                            >
                                {obj.tag}
                            </span>
                        }
                    </div>)}
            </div>
            {total > 0 &&
                <div className="czb__progress-wrap">
                    <div className="czb__progress-bar">
                        <div className="czb__progress-fill" style={{ width: `${progressPct}%` }} />
                    </div>
                    <span className="czb__progress-stat">{doneCount}/{total} completed</span>
                </div>
            }
        </div>
    );
}

// ─── Leaderboard Mini ──────────────────────────────────────────────────────────

function LeaderboardMiniMechanic({ entries }: { entries: ChallengeZoneLeaderEntry[] }) {
    const displayed = entries.slice(0, 7);
    const currentUser = entries.find(e => e.isCurrentUser);
    const showCurrentUserSeparately = currentUser && !displayed.find(e => e.isCurrentUser);

    return (
        <div className="czb__leaderboard">
            {displayed.map((entry, i) =>
                <div key={i} className={`czb__lb-row${entry.isCurrentUser ? " czb__lb-row--me" : ""}${entry.rank <= 3 ? " czb__lb-row--top" : ""}`}>
                    <span className={`czb__lb-rank${entry.rank <= 3 ? ` czb__lb-rank--${entry.rank}` : ""}`}>{entry.rank}</span>
                    {entry.avatarUrl
                        ? <img src={entry.avatarUrl} alt={entry.name} className="czb__lb-avatar" />
                        : <div className="czb__lb-avatar czb__lb-avatar--placeholder" />
                    }
                    <span className="czb__lb-name">{entry.name}</span>
                    <span className="czb__lb-score">{entry.score.toLocaleString()}</span>
                </div>)}
            {showCurrentUserSeparately &&
                <>
                    <div className="czb__lb-sep">···</div>
                    <div className="czb__lb-row czb__lb-row--me">
                        <span className="czb__lb-rank">{currentUser.rank}</span>
                        {currentUser.avatarUrl
                            ? <img src={currentUser.avatarUrl} alt={currentUser.name} className="czb__lb-avatar" />
                            : <div className="czb__lb-avatar czb__lb-avatar--placeholder" />
                        }
                        <span className="czb__lb-name">{currentUser.name}</span>
                        <span className="czb__lb-score">{currentUser.score.toLocaleString()}</span>
                    </div>
                </>
            }
        </div>
    );
}

// ─── Bingo Mini ────────────────────────────────────────────────────────────────

function BingoMiniMechanic({ cells, perLineReward, completed, total }: {
    cells: ChallengeZoneBingoCell[];
    perLineReward?: string;
    completed: number;
    total: number;
}) {
    // Pad to 25 if fewer cells provided
    const padded: ChallengeZoneBingoCell[] = [...cells];
    while (padded.length < 25) {
        padded.push({ label: "", state: "unchecked" });
    }

    return (
        <div className="czb__bingo">
            <div className="czb__bingo-grid">
                {padded.slice(0, 25).map((cell, i) =>
                    <div
                        key={i}
                        className={`czb__bingo-cell czb__bingo-cell--${cell.state}`}
                    >
                        {cell.state === "free"
                            ? <span className="czb__bingo-free">FREE</span>
                            : cell.state === "done"
                                ? <span className="czb__bingo-check">✓</span>
                                : <span className="czb__bingo-label">{cell.label}</span>
                        }
                    </div>)}
            </div>
            <div className="czb__bingo-stats">
                <span>{completed} of {total} completed</span>
                {perLineReward && <span className="czb__bingo-reward">{perLineReward}</span>}
            </div>
        </div>
    );
}

// ─── Countdown Timer ───────────────────────────────────────────────────────────

function CountdownTimer({ endAt }: { endAt: number }) {
    const [remaining, setRemaining] = useState(() => Math.max(0, endAt - Date.now()));

    useEffect(() => {
        if (remaining <= 0) return;
        const interval = setInterval(() => {
            const r = Math.max(0, endAt - Date.now());
            setRemaining(r);
            if (r <= 0) clearInterval(interval);
        }, 1000);
        return () => clearInterval(interval);
    }, [endAt]);

    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor(remaining % 86400000 / 3600000);
    const mins = Math.floor(remaining % 3600000 / 60000);
    const secs = Math.floor(remaining % 60000 / 1000);

    if (remaining <= 0) return <span className="czb__timer czb__timer--ended">ENDED</span>;

    return (
        <div className="czb__timer">
            {days > 0 && <span className="czb__timer-unit">{days}d</span>}
            <span className="czb__timer-unit">{String(hours).padStart(2, "0")}h</span>
            <span className="czb__timer-unit">{String(mins).padStart(2, "0")}m</span>
            <span className="czb__timer-unit">{String(secs).padStart(2, "0")}s</span>
        </div>
    );
}
